"use client";

import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Play,
  PlayCircle,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Pencil,
  Clock,
  Loader2,
  FlaskConical,
  ChevronDown,
  ChevronRight,
  Copy,
  X,
  ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listTestScenarios,
  createTestScenario,
  updateTestScenario,
  deleteTestScenario,
  deleteMultipleTestScenarios,
  runTestScenario,
  runAllTestScenarios,
} from "@/lib/api/test-scenarios";
import { listAllFormFieldGroups } from "@/lib/api/form-fields";
import { listAllOutputFieldGroups } from "@/lib/api/output-fields";
import { findReferenceTable, listReferenceTablesForVersion } from "@/lib/api/references";
import { getErrorMessage } from "@/lib/api/client";
import type {
  TestScenario,
  TestScenarioRunResult,
  TestScenarioRunAllResult,
  FormFieldGroupWithFields,
  OutputFieldGroupWithFields,
  FormField,
  OutputField,
  ConditionalRule,
} from "@/types";

interface TestScenariosTabProps {
  versionId: number;
  isEditable: boolean;
}

type ToleranceType = "percentage" | "absolute";

interface ExpectedOutput {
  value: unknown;
  tolerance?: number;
  tolerance_type?: ToleranceType;
}

interface ScenarioFormData {
  name: string;
  description: string;
  inputs: Record<string, unknown>;
  expected_outputs: Record<string, ExpectedOutput>;
}

// Helper to flatten form fields from groups (preserving group hierarchy order)
function flattenFormFields(groups: FormFieldGroupWithFields[]): FormField[] {
  const fields: FormField[] = [];
  
  // Sort groups by order first
  const sortedGroups = [...groups].sort((a, b) => a.order - b.order);
  
  const processGroup = (group: FormFieldGroupWithFields) => {
    // Add fields from this group, sorted by their order within the group
    const sortedFields = [...group.fields].sort((a, b) => a.order - b.order);
    fields.push(...sortedFields);
    
    // Process children groups in their order
    const sortedChildren = [...group.children].sort((a, b) => a.order - b.order);
    sortedChildren.forEach(processGroup);
  };
  
  sortedGroups.forEach(processGroup);
  return fields;
}

// Helper to flatten output fields from groups (preserving group hierarchy order)
function flattenOutputFields(groups: OutputFieldGroupWithFields[]): OutputField[] {
  const fields: OutputField[] = [];
  
  // Sort groups by order first
  const sortedGroups = [...groups].sort((a, b) => a.order - b.order);
  
  const processGroup = (group: OutputFieldGroupWithFields) => {
    // Add fields from this group, sorted by their order within the group
    const sortedFields = [...group.fields].sort((a, b) => a.order - b.order);
    fields.push(...sortedFields);
    
    // Process children groups in their order
    const sortedChildren = [...group.children].sort((a, b) => a.order - b.order);
    sortedChildren.forEach(processGroup);
  };
  
  sortedGroups.forEach(processGroup);
  return fields;
}

type SortMode = "default" | "name" | "max_percent_diff";

function getMaxAbsPercentDiff(result: TestScenarioRunResult): number {
  let maxDiff = 0;
  for (const r of Object.values(result.results)) {
    const expected = typeof r.expected === "number" ? r.expected : null;
    const actual = typeof r.actual === "number" ? r.actual : null;
    if (expected !== null && actual !== null && expected !== 0) {
      const pct = Math.abs(((actual - expected) / Math.abs(expected)) * 100);
      if (pct > maxDiff) maxDiff = pct;
    }
  }
  return maxDiff;
}

export function TestScenariosTab({ versionId, isEditable }: TestScenariosTabProps) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState<TestScenario | null>(null);
  const [expandedInputs, setExpandedInputs] = useState(true);
  const [expandedOutputs, setExpandedOutputs] = useState(true);
  const [runResults, setRunResults] = useState<Record<number, TestScenarioRunResult>>({});
  const [allRunResult, setAllRunResult] = useState<TestScenarioRunAllResult | null>(null);
  const [isRunningAll, setIsRunningAll] = useState(false);
  const [runningScenarioId, setRunningScenarioId] = useState<number | null>(null);
  const [selectedScenarioIds, setSelectedScenarioIds] = useState<Set<number>>(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<"single" | "multiple">("single");
  const [singleDeleteId, setSingleDeleteId] = useState<number | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>("default");

  const [formData, setFormData] = useState<ScenarioFormData>({
    name: "",
    description: "",
    inputs: {},
    expected_outputs: {},
  });

  // Track raw string values for numeric inputs (to allow typing decimals like "1." or "1,")
  const [rawExpectedValues, setRawExpectedValues] = useState<Record<string, string>>({});
  const [rawToleranceValues, setRawToleranceValues] = useState<Record<string, string>>({});

  // Fetch test scenarios
  const { data: scenariosData, isLoading: loadingScenarios } = useQuery({
    queryKey: ["test-scenarios", versionId],
    queryFn: () => listTestScenarios(versionId),
  });

  // Fetch form fields for input definition
  const { data: formFieldGroups } = useQuery({
    queryKey: ["form-field-groups", versionId],
    queryFn: () => listAllFormFieldGroups(versionId),
  });

  // Fetch output fields for expected outputs definition
  const { data: outputFieldGroups } = useQuery({
    queryKey: ["output-field-groups", versionId],
    queryFn: () => listAllOutputFieldGroups(versionId),
  });

  // Grilles de la version, résolues comme dans la simulation et le moteur (voir findReferenceTable)
  const { data: referenceTables } = useQuery({
    queryKey: ["version-reference-tables", versionId, "simulation"],
    queryFn: () => listReferenceTablesForVersion(versionId),
    // Rechargées à chaque ouverture : une grille modifiée juste avant doit s'afficher telle que
    // le moteur la lira, et sa modification n'invalide pas cette clé.
    staleTime: 0,
  });

  const formFields = useMemo(
    () => (formFieldGroups ? flattenFormFields(formFieldGroups) : []),
    [formFieldGroups]
  );

  const outputFields = useMemo(
    () => (outputFieldGroups ? flattenOutputFields(outputFieldGroups) : []),
    [outputFieldGroups]
  );

  // Get ALL matching conditional constraints for a field based on source field value
  // Returns an array to support multiple rules with the same when_value (e.g., multiple reference tables)
  const getAppliedConstraints = (field: FormField): ConditionalRule[] => {
    if (!field.conditional_constraints) return [];
    
    const { source_field_slug, rules } = field.conditional_constraints;
    const sourceValue = formData.inputs[source_field_slug];
    
    // Handle undefined/null but NOT empty string or false (which are valid values)
    if (sourceValue === undefined || sourceValue === null) {
      return [];
    }
    
    // Find ALL rules that match the source field value
    // Handle boolean values (from checkbox fields) and other types
    const matchingRules = rules.filter((rule) => {
      // For boolean comparisons
      if (typeof rule.when_value === "boolean" || typeof sourceValue === "boolean") {
        return rule.when_value === sourceValue;
      }
      // For string/number comparisons
      return String(rule.when_value) === String(sourceValue);
    });
    return matchingRules;
  };

  // Get options for a select/multiselect field (from enum_values or reference table)
  // Also applies conditional constraints if defined (including dynamic reference tables)
  // allowed_values in constraints are ADDED to options (not used as filter)
  // Returns {label, value} pairs: label is for display (reference_column), value is for storage (reference_value_column)
  const getSelectOptions = (field: FormField): { label: string; value: string }[] => {
    let options: { label: string; value: string }[] = [];
    
    // Get ALL applied conditional constraints (can be multiple rules with same when_value)
    const appliedConstraints = getAppliedConstraints(field);
    
    // Check if any conditional constraints define reference tables
    const constraintsWithRefTables = appliedConstraints.filter(
      (c) => c.reference_table_id && c.reference_column
    );
    
    if (constraintsWithRefTables.length > 0 && referenceTables) {
      const allOptions: { label: string; value: string }[] = [];
      for (const constraint of constraintsWithRefTables) {
        const table = findReferenceTable(referenceTables, constraint.reference_table_id);
        if (table?.data) {
          const displayColumn = constraint.reference_column!;
          const valueColumn = constraint.reference_value_column || displayColumn;
          const tableOptions = table.data
            .filter((row) => {
              const val = row[displayColumn];
              return val !== null && val !== undefined && val !== "";
            })
            .map((row) => ({
              label: String(row[displayColumn]),
              value: String(row[valueColumn] ?? row[displayColumn]),
            }));
          allOptions.push(...tableOptions);
        }
      }
      options = allOptions;
    }
    // If field has enum_values, use them directly
    else if (field.enum_values && field.enum_values.length > 0) {
      options = field.enum_values.filter((v) => v !== "").map((v) => ({ label: v, value: v }));
    }
    // If field references a table, get options from reference table data
    else if (field.reference_table_id && field.reference_column && referenceTables) {
      const table = findReferenceTable(referenceTables, field.reference_table_id);
      if (table?.data) {
        let filteredData = table.data;
        if (field.reference_filter?.column && field.reference_filter.values.length > 0) {
          filteredData = table.data.filter((row) => {
            const cellValue = String(row[field.reference_filter!.column] ?? "");
            return field.reference_filter!.values.includes(cellValue);
          });
        }
        const displayColumn = field.reference_column!;
        const valueColumn = field.reference_value_column || displayColumn;
        const seen = new Set<string>();
        options = filteredData
          .filter((row) => {
            const val = row[displayColumn];
            return val !== null && val !== undefined && val !== "";
          })
          .map((row) => ({
            label: String(row[displayColumn]),
            value: String(row[valueColumn] ?? row[displayColumn]),
          }))
          .filter((opt) => {
            if (seen.has(opt.value) || opt.value === "") return false;
            seen.add(opt.value);
            return true;
          });
      }
    }
    
    // Add allowed_values from conditional constraints to the options
    // This allows adding extra options (like "Autre véhicule") on top of reference table values
    for (const constraint of appliedConstraints) {
      if (constraint.allowed_values && constraint.allowed_values.length > 0) {
        options.push(...constraint.allowed_values.map((v) => ({ label: v, value: v })));
      }
    }

    // Return unique options by value (excluding empty values)
    const seen = new Set<string>();
    return options.filter((opt) => {
      if (seen.has(opt.value) || opt.value === "") return false;
      seen.add(opt.value);
      return true;
    });
  };

  const invalidateScenarios = () => {
    queryClient.invalidateQueries({ queryKey: ["test-scenarios", versionId] });
  };

  const createMutation = useMutation({
    mutationFn: (data: ScenarioFormData) =>
      createTestScenario(versionId, {
        name: data.name,
        description: data.description || undefined,
        inputs: data.inputs,
        expected_outputs: data.expected_outputs,
      }),
    onSuccess: () => {
      invalidateScenarios();
      toast.success("Scénario de test créé");
      closeDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ScenarioFormData }) =>
      updateTestScenario(id, {
        name: data.name,
        description: data.description || undefined,
        inputs: data.inputs,
        expected_outputs: data.expected_outputs,
      }),
    onSuccess: () => {
      invalidateScenarios();
      toast.success("Scénario de test mis à jour");
      closeDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteTestScenario(id),
    onSuccess: () => {
      invalidateScenarios();
      toast.success("Scénario de test supprimé");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteMultipleMutation = useMutation({
    mutationFn: (ids: number[]) => deleteMultipleTestScenarios(versionId, ids),
    onSuccess: (_, ids) => {
      invalidateScenarios();
      setSelectedScenarioIds(new Set());
      toast.success(`${ids.length} scénario(s) de test supprimé(s)`);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const runMutation = useMutation({
    mutationFn: (id: number) => runTestScenario(id),
    onSuccess: (result) => {
      setRunResults((prev) => ({ ...prev, [result.scenario_id]: result }));
      if (result.passed) {
        toast.success(`Test "${result.scenario_name}" réussi`, {
          description: `Exécuté en ${result.execution_time_ms}ms`,
        });
      } else if (result.errors.length > 0) {
        toast.error(`Test "${result.scenario_name}" en erreur`, {
          description: result.errors[0],
        });
      } else {
        toast.error(`Test "${result.scenario_name}" échoué`, {
          description: "Certains résultats ne correspondent pas aux attentes",
        });
      }
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
    onSettled: () => setRunningScenarioId(null),
  });

  const runAllMutation = useMutation({
    mutationFn: () => runAllTestScenarios(versionId),
    onSuccess: (result) => {
      setAllRunResult(result);
      // Update individual results
      const newResults: Record<number, TestScenarioRunResult> = {};
      result.results.forEach((r) => {
        newResults[r.scenario_id] = r;
      });
      setRunResults(newResults);

      if (result.failed === 0 && result.errors === 0) {
        toast.success("Tous les tests ont réussi", {
          description: `${result.passed}/${result.total} tests passés en ${result.execution_time_ms}ms`,
        });
      } else {
        toast.error("Certains tests ont échoué", {
          description: `${result.passed} réussis, ${result.failed} échoués, ${result.errors} erreurs`,
        });
      }
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
    onSettled: () => setIsRunningAll(false),
  });

  const closeDialog = () => {
    setDialogOpen(false);
    setIsEditing(false);
    setSelectedScenario(null);
    setFormData({
      name: "",
      description: "",
      inputs: {},
      expected_outputs: {},
    });
    setRawExpectedValues({});
    setRawToleranceValues({});
  };

  const openCreateDialog = () => {
    setIsEditing(false);
    setSelectedScenario(null);
    setFormData({
      name: "",
      description: "",
      inputs: {},
      expected_outputs: {},
    });
    setRawExpectedValues({});
    setRawToleranceValues({});
    setDialogOpen(true);
  };

  const openEditDialog = (scenario: TestScenario) => {
    setIsEditing(true);
    setSelectedScenario(scenario);
    
    // Convert expected_outputs to proper format
    const expectedOutputs: Record<string, ExpectedOutput> = {};
    const rawValues: Record<string, string> = {};
    const rawTolerances: Record<string, string> = {};
    
    for (const [key, val] of Object.entries(scenario.expected_outputs)) {
      if (typeof val === "object" && val !== null && "value" in val) {
        const typedVal = val as { value: unknown; tolerance?: number; tolerance_type?: ToleranceType };
        expectedOutputs[key] = {
          value: typedVal.value,
          tolerance: typedVal.tolerance,
          tolerance_type: typedVal.tolerance_type || "percentage",
        };
        // Initialize raw values from existing data
        if (typedVal.value !== undefined && typedVal.value !== null) {
          rawValues[key] = String(typedVal.value);
        }
        if (typedVal.tolerance !== undefined) {
          rawTolerances[key] = String(typedVal.tolerance);
        }
      } else {
        expectedOutputs[key] = { value: val };
        if (val !== undefined && val !== null) {
          rawValues[key] = String(val);
        }
      }
    }
    
    setFormData({
      name: scenario.name,
      description: scenario.description || "",
      inputs: { ...scenario.inputs },
      expected_outputs: expectedOutputs,
    });
    setRawExpectedValues(rawValues);
    setRawToleranceValues(rawTolerances);
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (isEditing && selectedScenario) {
      updateMutation.mutate({ id: selectedScenario.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleRunScenario = (id: number) => {
    setRunningScenarioId(id);
    runMutation.mutate(id);
  };

  const handleRunAll = () => {
    setIsRunningAll(true);
    setAllRunResult(null);
    runAllMutation.mutate();
  };

  const toggleScenarioSelection = (id: number) => {
    setSelectedScenarioIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = (scenarios: TestScenario[]) => {
    if (selectedScenarioIds.size === scenarios.length) {
      setSelectedScenarioIds(new Set());
    } else {
      setSelectedScenarioIds(new Set(scenarios.map((s) => s.id)));
    }
  };

  const confirmDeleteSingle = (id: number) => {
    setSingleDeleteId(id);
    setDeleteTarget("single");
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteMultiple = () => {
    setDeleteTarget("multiple");
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteTarget === "single" && singleDeleteId !== null) {
      deleteMutation.mutate(singleDeleteId);
    } else if (deleteTarget === "multiple") {
      deleteMultipleMutation.mutate(Array.from(selectedScenarioIds));
    }
    setDeleteConfirmOpen(false);
    setSingleDeleteId(null);
  };

  const copyResultsToClipboard = (scenario: TestScenario) => {
    const result = runResults[scenario.id];
    
    const jsonData = {
      scenario: {
        id: scenario.id,
        name: scenario.name,
        description: scenario.description,
      },
      inputs: scenario.inputs,
      expected_outputs: scenario.expected_outputs,
      ...(result && {
        execution: {
          passed: result.passed,
          execution_time_ms: result.execution_time_ms,
          results: Object.fromEntries(
            Object.entries(result.results).map(([slug, r]) => [
              slug,
              {
                expected: r.expected,
                actual: r.actual,
                passed: r.passed,
                difference: r.difference,
              },
            ])
          ),
          errors: result.errors.length > 0 ? result.errors : undefined,
        },
      }),
    };

    navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
    toast.success("Résultats copiés", {
      description: "Le JSON a été copié dans le presse-papiers",
    });
  };

  const updateInput = (slug: string, value: unknown) => {
    setFormData((prev) => {
      const newInputs = { ...prev.inputs, [slug]: value };
      
      // Reset dependent select/multiselect fields that have conditional constraints based on this field
      formFields.forEach((field) => {
        if (
          field.conditional_constraints?.source_field_slug === slug &&
          (field.field_type === "select" || field.field_type === "multiselect")
        ) {
          // Check if any rule has a reference_table_id (meaning options source can change)
          const hasConditionalRefTable = field.conditional_constraints.rules.some(
            (rule) => rule.reference_table_id
          );
          if (hasConditionalRefTable) {
            // Reset the field value since the options source changed
            newInputs[field.slug] = field.field_type === "multiselect" ? [] : undefined;
          }
        }
      });
      
      return {
        ...prev,
        inputs: newInputs,
      };
    });
  };

  const updateExpectedOutput = (
    slug: string,
    value: unknown,
    tolerance?: number,
    tolerance_type?: ToleranceType
  ) => {
    setFormData((prev) => {
      const current = prev.expected_outputs[slug];
      const newOutput: ExpectedOutput = { value };
      
      // Keep existing tolerance_type if not provided
      const effectiveToleranceType = tolerance_type ?? current?.tolerance_type ?? "percentage";
      
      if (tolerance !== undefined) {
        newOutput.tolerance = tolerance;
        newOutput.tolerance_type = effectiveToleranceType;
      } else if (current?.tolerance !== undefined) {
        // Keep existing tolerance if not updated
        newOutput.tolerance = current.tolerance;
        newOutput.tolerance_type = effectiveToleranceType;
      }
      
      return {
        ...prev,
        expected_outputs: {
          ...prev.expected_outputs,
          [slug]: newOutput,
        },
      };
    });
  };

  const removeExpectedOutput = (slug: string) => {
    setFormData((prev) => {
      const newOutputs = { ...prev.expected_outputs };
      delete newOutputs[slug];
      return { ...prev, expected_outputs: newOutputs };
    });
  };

  const getScenarioStatus = (scenario: TestScenario) => {
    const result = runResults[scenario.id];
    if (!result) return "untested";
    if (result.errors.length > 0) return "error";
    return result.passed ? "passed" : "failed";
  };

  const renderInputField = (field: FormField) => {
    const value = formData.inputs[field.slug];

    switch (field.field_type) {
      case "number":
        return (
          <Input
            type="number"
            value={value !== undefined ? String(value) : ""}
            onChange={(e) =>
              updateInput(
                field.slug,
                e.target.value ? parseFloat(e.target.value) : undefined
              )
            }
            placeholder={field.placeholder || undefined}
            min={field.min_value ?? undefined}
            max={field.max_value ?? undefined}
          />
        );
      case "checkbox":
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={Boolean(value)}
              onCheckedChange={(checked) => updateInput(field.slug, checked)}
            />
            <span className="text-sm text-muted-foreground">
              {value ? "Oui" : "Non"}
            </span>
          </div>
        );
      case "select": {
        const options = getSelectOptions(field);
        const hasValue = value !== undefined && value !== "";
        return (
          <div className="flex items-center gap-1">
            <Select
              value={value !== undefined ? String(value) : ""}
              onValueChange={(v) => updateInput(field.slug, v)}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {options.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasValue && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => updateInput(field.slug, undefined)}
                title="Effacer la sélection"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
          </div>
        );
      }
      case "multiselect": {
        const options = getSelectOptions(field);
        const selectedValues = Array.isArray(value) ? value : [];
        const hasMultiselectValue = selectedValues.length > 0;
        return (
          <div className="space-y-2">
            {hasMultiselectValue && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground"
                onClick={() => updateInput(field.slug, undefined)}
              >
                <X className="h-3 w-3 mr-1" />
                Tout effacer
              </Button>
            )}
            {options.map((opt) => (
              <div key={opt.value} className="flex items-center gap-2">
                <Checkbox
                  checked={selectedValues.includes(opt.value)}
                  onCheckedChange={(checked) => {
                    const newValues = checked
                      ? [...selectedValues, opt.value]
                      : selectedValues.filter((v: string) => v !== opt.value);
                    updateInput(field.slug, newValues.length > 0 ? newValues : undefined);
                  }}
                />
                <span className="text-sm">{opt.label}</span>
              </div>
            ))}
          </div>
        );
      }
      case "date": {
        const hasDateValue = value !== undefined && value !== "";
        return (
          <div className="flex items-center gap-1">
            <Input
              type="date"
              value={value !== undefined ? String(value) : ""}
              onChange={(e) => updateInput(field.slug, e.target.value || undefined)}
              className="flex-1"
            />
            {hasDateValue && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => updateInput(field.slug, undefined)}
                title="Effacer la date"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
          </div>
        );
      }
      case "textarea":
        return (
          <Textarea
            value={value !== undefined ? String(value) : ""}
            onChange={(e) =>
              updateInput(field.slug, e.target.value || undefined)
            }
            placeholder={field.placeholder || undefined}
            rows={2}
          />
        );
      default:
        return (
          <Input
            type="text"
            value={value !== undefined ? String(value) : ""}
            onChange={(e) =>
              updateInput(field.slug, e.target.value || undefined)
            }
            placeholder={field.placeholder || undefined}
          />
        );
    }
  };

  const scenarios = scenariosData?.items || [];

  const sortedScenarios = useMemo(() => {
    if (sortMode === "default") return scenarios;
    const sorted = [...scenarios];
    if (sortMode === "name") {
      sorted.sort((a, b) => a.name.localeCompare(b.name, "fr"));
    } else if (sortMode === "max_percent_diff") {
      sorted.sort((a, b) => {
        const diffA = runResults[a.id] ? getMaxAbsPercentDiff(runResults[a.id]) : -1;
        const diffB = runResults[b.id] ? getMaxAbsPercentDiff(runResults[b.id]) : -1;
        return diffB - diffA;
      });
    }
    return sorted;
  }, [scenarios, sortMode, runResults]);

  if (loadingScenarios) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Scénarios de test</CardTitle>
          <CardDescription>
            Définissez des cas de test avec des entrées et résultats attendus
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {isEditable && selectedScenarioIds.size > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={confirmDeleteMultiple}
              disabled={deleteMultipleMutation.isPending}
            >
              {deleteMultipleMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Supprimer ({selectedScenarioIds.size})
            </Button>
          )}
          {scenarios.length > 0 && (
            <Button
              variant="outline"
              onClick={handleRunAll}
              disabled={isRunningAll}
            >
              {isRunningAll ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <PlayCircle className="mr-2 h-4 w-4" />
              )}
              Exécuter tous
            </Button>
          )}
          {isEditable && (
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Ajouter
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary of last run all */}
        {allRunResult && (
          <div
            className={`mb-6 p-4 rounded-lg border ${
              allRunResult.failed === 0 && allRunResult.errors === 0
                ? "bg-success/10 border-success/20"
                : "bg-error/10 border-error/20"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {allRunResult.failed === 0 && allRunResult.errors === 0 ? (
                  <CheckCircle2 className="h-5 w-5 text-success" />
                ) : (
                  <XCircle className="h-5 w-5 text-error" />
                )}
                <div>
                  <p className="font-medium">
                    {allRunResult.passed}/{allRunResult.total} tests réussis
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {allRunResult.failed > 0 && `${allRunResult.failed} échoués`}
                    {allRunResult.failed > 0 && allRunResult.errors > 0 && ", "}
                    {allRunResult.errors > 0 && `${allRunResult.errors} erreurs`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                {allRunResult.execution_time_ms}ms
              </div>
            </div>
          </div>
        )}

        {allRunResult && scenarios.length > 1 && (
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
            <Select value={sortMode} onValueChange={(v) => setSortMode(v as SortMode)}>
              <SelectTrigger className="w-[220px] h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Ordre par défaut</SelectItem>
                <SelectItem value="name">Nom (A-Z)</SelectItem>
                <SelectItem value="max_percent_diff">Plus gros écart %</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {scenarios.length === 0 ? (
          <div className="text-center py-12">
            <FlaskConical className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">Aucun scénario de test</p>
            <p className="text-sm text-muted-foreground mt-2">
              Créez des scénarios pour valider le comportement du simulateur
            </p>
            {isEditable && (
              <Button variant="outline" className="mt-4" onClick={openCreateDialog}>
                Créer un scénario
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {isEditable && scenarios.length > 1 && (
              <div className="flex items-center gap-2 px-4 py-2 border rounded-lg bg-muted/30">
                <Checkbox
                  checked={selectedScenarioIds.size === scenarios.length}
                  onCheckedChange={() => toggleSelectAll(scenarios)}
                />
                <span className="text-sm text-muted-foreground">
                  {selectedScenarioIds.size === scenarios.length
                    ? "Tout désélectionner"
                    : "Tout sélectionner"}
                </span>
              </div>
            )}
            {sortedScenarios.map((scenario) => {
              const status = getScenarioStatus(scenario);
              const result = runResults[scenario.id];
              const isRunning = runningScenarioId === scenario.id;
              const isSelected = selectedScenarioIds.has(scenario.id);

              return (
                <div
                  key={scenario.id}
                  className={`p-4 rounded-lg border ${
                    isSelected
                      ? "border-primary/50 bg-primary/5"
                      : status === "passed"
                      ? "border-success/30 bg-success/5"
                      : status === "failed"
                      ? "border-error/30 bg-error/5"
                      : status === "error"
                      ? "border-warning/30 bg-warning/5"
                      : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      {isEditable && (
                        <div className="pt-2.5">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleScenarioSelection(scenario.id)}
                          />
                        </div>
                      )}
                      <div
                        className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                          status === "passed"
                            ? "bg-success/10"
                            : status === "failed"
                            ? "bg-error/10"
                            : status === "error"
                            ? "bg-warning/10"
                            : "bg-muted"
                        }`}
                      >
                        {status === "passed" ? (
                          <CheckCircle2 className="h-5 w-5 text-success" />
                        ) : status === "failed" ? (
                          <XCircle className="h-5 w-5 text-error" />
                        ) : status === "error" ? (
                          <AlertCircle className="h-5 w-5 text-warning" />
                        ) : (
                          <FlaskConical className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="font-medium hover:text-primary hover:underline transition-colors text-left"
                            onClick={() => openEditDialog(scenario)}
                          >
                            {scenario.name}
                          </button>
                          {status === "passed" && (
                            <Badge variant="success" className="text-xs">
                              Réussi
                            </Badge>
                          )}
                          {status === "failed" && (
                            <Badge variant="destructive" className="text-xs">
                              Échoué
                            </Badge>
                          )}
                          {status === "error" && (
                            <Badge variant="warning" className="text-xs">
                              Erreur
                            </Badge>
                          )}
                        </div>
                        {scenario.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {scenario.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>
                            {Object.keys(scenario.inputs).length} entrée(s)
                          </span>
                          <span>
                            {Object.keys(scenario.expected_outputs).length} sortie(s)
                            attendue(s)
                          </span>
                          {result && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {result.execution_time_ms}ms
                            </span>
                          )}
                        </div>

                        {/* Show detailed results if test passed */}
                        {result && result.passed && Object.keys(result.results).length > 0 && (
                          <div className="mt-3 p-3 rounded bg-success/10 border border-success/20 text-sm">
                            <p className="font-medium mb-2 text-success-dark">Rapport de test :</p>
                            <div className="space-y-2">
                              {Object.entries(result.results)
                                .sort(([slugA], [slugB]) => {
                                  // Sort by output field order
                                  const orderA = outputFields.findIndex(f => f.slug === slugA);
                                  const orderB = outputFields.findIndex(f => f.slug === slugB);
                                  // Fields not found in outputFields go to the end
                                  const posA = orderA === -1 ? Infinity : orderA;
                                  const posB = orderB === -1 ? Infinity : orderB;
                                  return posA - posB;
                                })
                                .map(([slug, r]) => {
                                const expected = typeof r.expected === 'number' ? r.expected : null;
                                const actual = typeof r.actual === 'number' ? r.actual : null;
                                const hasNumericValues = expected !== null && actual !== null;
                                const percentDiff = hasNumericValues && expected !== 0 
                                  ? ((actual - expected) / Math.abs(expected)) * 100 
                                  : null;
                                
                                return (
                                  <div key={slug} className="flex items-start gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                      <code className="text-xs bg-success/20 px-1.5 py-0.5 rounded font-medium">
                                        {slug}
                                      </code>
                                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5 text-xs">
                                        <div>
                                          <span className="text-muted-foreground">Attendu:</span>{" "}
                                          <span className="font-mono font-medium">
                                            {typeof r.expected === 'number' 
                                              ? r.expected.toLocaleString('fr-FR', { maximumFractionDigits: 4 })
                                              : JSON.stringify(r.expected)}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-muted-foreground">Obtenu:</span>{" "}
                                          <span className="font-mono font-medium text-success">
                                            {typeof r.actual === 'number' 
                                              ? r.actual.toLocaleString('fr-FR', { maximumFractionDigits: 4 })
                                              : JSON.stringify(r.actual)}
                                          </span>
                                        </div>
                                        {hasNumericValues && (
                                          <>
                                            <div>
                                              <span className="text-muted-foreground">Écart:</span>{" "}
                                              <span className="font-mono">
                                                {r.difference !== null && r.difference !== undefined
                                                  ? (r.difference >= 0 ? '+' : '') + r.difference.toLocaleString('fr-FR', { maximumFractionDigits: 4 })
                                                  : (actual - expected).toLocaleString('fr-FR', { maximumFractionDigits: 4 })}
                                              </span>
                                            </div>
                                            {percentDiff !== null && (
                                              <div>
                                                <span className="text-muted-foreground">Écart %:</span>{" "}
                                                <span className="font-mono">
                                                  {(percentDiff >= 0 ? '+' : '') + percentDiff.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                                                </span>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Show detailed results if test failed */}
                        {result && !result.passed && Object.keys(result.results).length > 0 && (
                          <div className="mt-3 p-3 rounded bg-muted/50 text-sm">
                            <p className="font-medium mb-2">Rapport de test :</p>
                            <div className="space-y-2">
                              {Object.entries(result.results)
                                .sort(([slugA], [slugB]) => {
                                  // Sort by output field order
                                  const orderA = outputFields.findIndex(f => f.slug === slugA);
                                  const orderB = outputFields.findIndex(f => f.slug === slugB);
                                  // Fields not found in outputFields go to the end
                                  const posA = orderA === -1 ? Infinity : orderA;
                                  const posB = orderB === -1 ? Infinity : orderB;
                                  return posA - posB;
                                })
                                .map(([slug, r]) => {
                                  const outputField = outputFields.find(f => f.slug === slug);
                                  const expected = typeof r.expected === 'number' ? r.expected : null;
                                  const actual = typeof r.actual === 'number' ? r.actual : null;
                                  const hasNumericValues = expected !== null && actual !== null;
                                  const percentDiff = hasNumericValues && expected !== 0 
                                    ? ((actual - expected) / Math.abs(expected)) * 100 
                                    : null;
                                  
                                  return (
                                    <div key={slug} className={`flex items-start gap-2 ${r.passed ? '' : 'bg-error/10 p-2 rounded'}`}>
                                      {r.passed ? (
                                        <CheckCircle2 className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                                      ) : (
                                        <XCircle className="h-4 w-4 text-error mt-0.5 flex-shrink-0" />
                                      )}
                                      <div className="flex-1">
                                        <div className="flex items-center gap-1.5">
                                          <code className={`text-xs px-1.5 py-0.5 rounded font-medium ${r.passed ? 'bg-success/20' : 'bg-error/20'}`}>
                                            {slug}
                                          </code>
                                          {outputField?.excel_source && (
                                            <span className="text-xs text-success font-mono">
                                              [{outputField.excel_source}]
                                            </span>
                                          )}
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5 text-xs">
                                          <div>
                                            <span className="text-muted-foreground">Attendu:</span>{" "}
                                            <span className="font-mono font-medium">
                                              {typeof r.expected === 'number' 
                                                ? r.expected.toLocaleString('fr-FR', { maximumFractionDigits: 4 })
                                                : JSON.stringify(r.expected)}
                                            </span>
                                          </div>
                                          <div>
                                            <span className="text-muted-foreground">Obtenu:</span>{" "}
                                            <span className={`font-mono font-medium ${r.passed ? 'text-success' : 'text-error'}`}>
                                              {typeof r.actual === 'number' 
                                                ? r.actual.toLocaleString('fr-FR', { maximumFractionDigits: 4 })
                                                : JSON.stringify(r.actual)}
                                            </span>
                                          </div>
                                          {hasNumericValues && (
                                            <>
                                              <div>
                                                <span className="text-muted-foreground">Écart:</span>{" "}
                                                <span className={`font-mono ${!r.passed ? 'text-error' : ''}`}>
                                                  {r.difference !== null && r.difference !== undefined
                                                    ? (r.difference >= 0 ? '+' : '') + r.difference.toLocaleString('fr-FR', { maximumFractionDigits: 4 })
                                                    : (actual - expected).toLocaleString('fr-FR', { maximumFractionDigits: 4 })}
                                                </span>
                                              </div>
                                              {percentDiff !== null && (
                                                <div>
                                                  <span className="text-muted-foreground">Écart %:</span>{" "}
                                                  <span className={`font-mono ${!r.passed ? 'text-error' : ''}`}>
                                                    {(percentDiff >= 0 ? '+' : '') + percentDiff.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                                                  </span>
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}

                        {/* Show errors */}
                        {result && result.errors.length > 0 && (
                          <div className="mt-3 p-3 rounded bg-warning/10 text-sm space-y-3">
                            <p className="font-medium text-warning">
                              Erreurs :
                            </p>
                            {result.errors.map((err, i) => (
                              <p key={i} className="text-warning">
                                {err}
                              </p>
                            ))}
                            
                            {/* Show detailed error info */}
                            {result.error_details && result.error_details.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-warning/20">
                                <p className="font-medium text-warning-dark mb-2">Détails du débogage :</p>
                                {result.error_details.map((detail, i) => (
                                  <div key={i} className="space-y-1 text-xs bg-warning/10 p-2 rounded">
                                    {detail.field_slug && (
                                      <p>
                                        <span className="font-medium">Champ : </span>
                                        <code className="bg-muted px-1 rounded">{detail.field_slug}</code>
                                      </p>
                                    )}
                                    {detail.formula && (
                                      <p>
                                        <span className="font-medium">Formule : </span>
                                        <code className="bg-muted px-1 rounded break-all">{detail.formula}</code>
                                      </p>
                                    )}
                                    {detail.null_variables && detail.null_variables.length > 0 && (
                                      <p className="text-error">
                                        <span className="font-medium">⚠️ Variables nulles (None) : </span>
                                        {detail.null_variables.map((v, j) => (
                                          <code key={j} className="bg-error/20 px-1 rounded mx-0.5">{v}</code>
                                        ))}
                                      </p>
                                    )}
                                    {detail.available_values && Object.keys(detail.available_values).length > 0 && (
                                      <details className="mt-1">
                                        <summary className="cursor-pointer font-medium text-warning-dark hover:text-warning-dark">
                                          Voir toutes les valeurs disponibles ({Object.keys(detail.available_values).length})
                                        </summary>
                                        <div className="mt-1 max-h-40 overflow-auto bg-muted/50 p-2 rounded">
                                          <pre className="text-[10px] whitespace-pre-wrap">
                                            {JSON.stringify(detail.available_values, null, 2)}
                                          </pre>
                                        </div>
                                      </details>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Show inputs used */}
                            {result.inputs_used && Object.keys(result.inputs_used).length > 0 && (
                              <details className="mt-2 pt-2 border-t border-warning/20">
                                <summary className="cursor-pointer font-medium text-warning-dark hover:text-warning-dark">
                                  Entrées utilisées ({Object.keys(result.inputs_used).length})
                                </summary>
                                <div className="mt-1 max-h-40 overflow-auto bg-muted/50 p-2 rounded">
                                  <pre className="text-[10px] whitespace-pre-wrap">
                                    {JSON.stringify(result.inputs_used, null, 2)}
                                  </pre>
                                </div>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRunScenario(scenario.id)}
                        disabled={isRunning || isRunningAll}
                        title="Exécuter le test"
                      >
                        {isRunning ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyResultsToClipboard(scenario)}
                        title="Copier les résultats en JSON"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      {isEditable && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(scenario)}
                            title="Modifier"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => confirmDeleteSingle(scenario.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Modifier le scénario" : "Nouveau scénario de test"}
            </DialogTitle>
            <DialogDescription>
              Définissez les valeurs d&apos;entrée et les résultats attendus
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto space-y-6 py-4">
            {/* Basic info */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom du scénario *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Ex: Test cas nominal"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Description du cas de test..."
                  rows={2}
                />
              </div>
            </div>

            {/* Inputs section */}
            <Collapsible open={expandedInputs} onOpenChange={setExpandedInputs}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between p-4 h-auto">
                  <div className="flex items-center gap-2">
                    {expandedInputs ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-medium">
                      Valeurs d&apos;entrée ({formFields.length} champs disponibles)
                    </span>
                  </div>
                  <Badge variant="secondary">
                    {Object.keys(formData.inputs).filter((k) => formData.inputs[k] !== undefined).length} défini(s)
                  </Badge>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="border rounded-lg p-4 mt-2">
                  {formFields.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Aucun champ de formulaire défini
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {formFields.map((field) => (
                        <div key={field.id} className="space-y-1">
                          <Label className="text-sm">
                            {field.label}
                            {field.required && (
                              <span className="text-destructive ml-1">*</span>
                            )}
                            {field.excel_source && (
                              <span className="ml-2 text-xs text-success font-mono">
                                [{field.excel_source}]
                              </span>
                            )}
                          </Label>
                          {renderInputField(field)}
                          <p className="text-xs text-muted-foreground">
                            <code>{field.slug}</code>
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Expected outputs section */}
            <Collapsible open={expandedOutputs} onOpenChange={setExpandedOutputs}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between p-4 h-auto">
                  <div className="flex items-center gap-2">
                    {expandedOutputs ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-medium">
                      Résultats attendus ({outputFields.length} champs de calcul)
                    </span>
                  </div>
                  <Badge variant="secondary">
                    {Object.keys(formData.expected_outputs).length} défini(s)
                  </Badge>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="border rounded-lg p-4 mt-2">
                  {outputFields.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Aucun champ de calcul défini
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {outputFields.map((field) => {
                        const expected = formData.expected_outputs[field.slug];
                        const isActive = expected !== undefined;

                        return (
                          <div
                            key={field.id}
                            className={`p-3 rounded-lg border ${
                              isActive ? "border-primary/50 bg-primary/5" : ""
                            }`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={isActive}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        // For numeric fields, set default tolerance to 0.01%
                                        const isNumeric =
                                          field.output_type === "number" ||
                                          field.output_type === "currency" ||
                                          field.output_type === "percentage";
                                        if (isNumeric) {
                                          updateExpectedOutput(field.slug, "", 0.01, "percentage");
                                          setRawToleranceValues(prev => ({ ...prev, [field.slug]: "0.01" }));
                                        } else {
                                          updateExpectedOutput(field.slug, "");
                                        }
                                        setRawExpectedValues(prev => ({ ...prev, [field.slug]: "" }));
                                      } else {
                                        removeExpectedOutput(field.slug);
                                        setRawExpectedValues(prev => {
                                          const { [field.slug]: _, ...rest } = prev;
                                          return rest;
                                        });
                                        setRawToleranceValues(prev => {
                                          const { [field.slug]: _, ...rest } = prev;
                                          return rest;
                                        });
                                      }
                                    }}
                                  />
                                  <Label className="text-sm font-medium">
                                    {field.label}
                                  </Label>
                                  <Badge variant="outline" className="text-xs">
                                    {field.output_type}
                                  </Badge>
                                  {field.excel_source && (
                                    <span className="text-xs text-success font-mono">
                                      [{field.excel_source}]
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 ml-6">
                                  <code>{field.slug}</code>
                                </p>
                              </div>

                              {isActive && (
                                <div className="flex items-center gap-2">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Valeur attendue</Label>
                                    <Input
                                      type="text"
                                      inputMode={
                                        field.output_type === "number" ||
                                        field.output_type === "currency" ||
                                        field.output_type === "percentage"
                                          ? "decimal"
                                          : "text"
                                      }
                                      value={rawExpectedValues[field.slug] ?? (expected?.value !== undefined ? String(expected.value) : "")}
                                      onChange={(e) => {
                                        const isNumeric =
                                          field.output_type === "number" ||
                                          field.output_type === "currency" ||
                                          field.output_type === "percentage";
                                        
                                        // Always update the raw value to allow typing decimals
                                        setRawExpectedValues(prev => ({ ...prev, [field.slug]: e.target.value }));
                                        
                                        if (isNumeric) {
                                          // Replace comma with dot for parsing
                                          const normalized = e.target.value.replace(",", ".");
                                          // Check if it's a valid complete number (not ending with . or ,)
                                          const isIncomplete = normalized.endsWith(".") || e.target.value.endsWith(",");
                                          if (!isIncomplete) {
                                            const val = normalized ? parseFloat(normalized) : undefined;
                                            if (normalized === "" || !isNaN(val as number)) {
                                              updateExpectedOutput(
                                                field.slug,
                                                val,
                                                expected?.tolerance
                                              );
                                            }
                                          }
                                        } else {
                                          updateExpectedOutput(
                                            field.slug,
                                            e.target.value,
                                            expected?.tolerance
                                          );
                                        }
                                      }}
                                      onBlur={(e) => {
                                        // On blur, ensure the final value is parsed and stored
                                        const isNumeric =
                                          field.output_type === "number" ||
                                          field.output_type === "currency" ||
                                          field.output_type === "percentage";
                                        if (isNumeric) {
                                          const normalized = e.target.value.replace(",", ".");
                                          const val = normalized ? parseFloat(normalized) : undefined;
                                          if (normalized === "" || !isNaN(val as number)) {
                                            updateExpectedOutput(
                                              field.slug,
                                              val,
                                              expected?.tolerance
                                            );
                                            // Update raw value to the normalized number
                                            setRawExpectedValues(prev => ({ 
                                              ...prev, 
                                              [field.slug]: val !== undefined ? String(val) : "" 
                                            }));
                                          }
                                        }
                                      }}
                                      className="w-32"
                                    />
                                  </div>
                                  {(field.output_type === "number" ||
                                    field.output_type === "currency" ||
                                    field.output_type === "percentage") && (
                                    <div className="flex items-end gap-2">
                                      <div className="space-y-1">
                                        <Label className="text-xs">Tolérance</Label>
                                        <div className="flex items-center gap-1">
                                          <Input
                                            type="text"
                                            inputMode="decimal"
                                            value={rawToleranceValues[field.slug] ?? (expected?.tolerance !== undefined ? String(expected.tolerance) : "")}
                                            onChange={(e) => {
                                              // Always update raw value first
                                              setRawToleranceValues(prev => ({ ...prev, [field.slug]: e.target.value }));
                                              
                                              const normalized = e.target.value.replace(",", ".");
                                              const isIncomplete = normalized.endsWith(".") || e.target.value.endsWith(",");
                                              if (!isIncomplete) {
                                                const tol = normalized ? parseFloat(normalized) : undefined;
                                                if (normalized === "" || !isNaN(tol as number)) {
                                                  updateExpectedOutput(
                                                    field.slug,
                                                    expected?.value,
                                                    tol,
                                                    expected?.tolerance_type
                                                  );
                                                }
                                              }
                                            }}
                                            onBlur={(e) => {
                                              const normalized = e.target.value.replace(",", ".");
                                              const tol = normalized ? parseFloat(normalized) : undefined;
                                              if (normalized === "" || !isNaN(tol as number)) {
                                                updateExpectedOutput(
                                                  field.slug,
                                                  expected?.value,
                                                  tol,
                                                  expected?.tolerance_type
                                                );
                                                setRawToleranceValues(prev => ({ 
                                                  ...prev, 
                                                  [field.slug]: tol !== undefined ? String(tol) : "" 
                                                }));
                                              }
                                            }}
                                            className="w-20"
                                            placeholder={expected?.tolerance_type === "absolute" ? "±0.1" : "±1"}
                                          />
                                          <Select
                                            value={expected?.tolerance_type || "percentage"}
                                            onValueChange={(v: ToleranceType) => {
                                              // Set default tolerance based on type if not already set
                                              const defaultTol = v === "percentage" ? 1 : 0.1;
                                              const newTol = expected?.tolerance ?? defaultTol;
                                              updateExpectedOutput(
                                                field.slug,
                                                expected?.value,
                                                newTol,
                                                v
                                              );
                                            }}
                                          >
                                            <SelectTrigger className="w-16">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="percentage">%</SelectItem>
                                              <SelectItem value="absolute">±</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !formData.name ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {createMutation.isPending || updateMutation.isPending
                ? "Enregistrement..."
                : isEditing
                ? "Mettre à jour"
                : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget === "single"
                ? "Êtes-vous sûr de vouloir supprimer ce scénario de test ? Cette action est irréversible."
                : `Êtes-vous sûr de vouloir supprimer ${selectedScenarioIds.size} scénario(s) de test ? Cette action est irréversible.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

